/*
 * pt-BR UI strings for the web app. Must define the exact same key set as
 * `en.ts` (enforced by strings.test.ts). Values reuse the reviewed mobile
 * translations (app/src/i18n/locales/pt-BR.ts) wherever the meaning matches;
 * web-only strings were translated in the same register — calm, direct,
 * informal "você".
 */
const ptBR: Record<string, string> = {
  // Common
  "common.retry": "Tentar novamente",
  "common.cancel": "Cancelar",
  "common.close": "Fechar",
  "common.back": "Voltar",
  "common.save": "Salvar",
  "common.delete": "Excluir",
  "common.done": "Pronto",
  "common.loading": "Carregando…",
  "common.loadingMore": "Carregando mais…",
  "common.couldntLoadMore": "Não foi possível carregar mais.",
  "common.backToLibrary": "Voltar para a Biblioteca",
  "common.findSomething": "Encontrar algo para assistir",
  "common.optional": "(opcional)",
  "common.film": "Filme",
  "common.series": "Série",
  "common.viewProfile": "{{name}}, ver perfil",
  "common.openTitle": "Abrir {{name}}",
  "common.avatarOf": "Avatar de {{name}}",
  "common.skipToContent": "Pular para o conteúdo",
  "common.notBuiltYet": "Ainda não construída: esta tela é um esboço enquanto a base do app web é entregue.",

  // Relative time
  "time.justNow": "agora mesmo",
  "time.minutesAgo": "há {{count}} min",
  "time.hoursAgo": "há {{count}} h",
  "time.daysAgo": "há {{count}} d",

  // Theme
  "theme.label": "Tema",
  "theme.light": "Claro",
  "theme.system": "Sistema",
  "theme.dark": "Escuro",

  // Navigation
  "nav.primary": "Principal",
  "nav.library": "Biblioteca",
  "nav.discover": "Descobrir",
  "nav.friends": "Amigos",
  "nav.alerts": "Alertas",
  "nav.profile": "Perfil",
  "nav.accountMenu": "Menu da conta de {{name}}",
  "nav.viewProfile": "Ver perfil",
  "nav.importExport": "Importar / exportar",
  "nav.unreadBadge": "{{label}}, {{count}} notificações não lidas",

  // Library
  "library.title": "Sua Biblioteca",
  "library.overline": "Biblioteca",
  "library.greeting.morning": "Bom dia",
  "library.greeting.afternoon": "Boa tarde",
  "library.greeting.evening": "Boa noite",
  "library.filterByStatus": "Filtrar por status",
  "library.filter.all": "Todos",
  "library.filter.watchlist": "Quero ver",
  "library.filter.watching": "Assistindo",
  "library.filter.completed": "Finalizados",
  "library.filter.dropped": "Abandonados",
  "library.filterByType": "Filtrar por tipo",
  "library.type.all": "Tudo",
  "library.type.series": "Séries",
  "library.type.movies": "Filmes",
  "library.sortBy": "Ordenar por",
  "library.sort.recent": "Recentes",
  "library.sort.title": "Título",
  "library.sort.progress": "Progresso",
  "library.collectionView": "Visualização da coleção",
  "library.viewAsGrid": "Ver em grade",
  "library.viewAsList": "Ver em lista",
  "library.loading": "Carregando sua biblioteca",
  "library.empty.all.overline": "Sua biblioteca",
  "library.empty.all.body": "Sua biblioteca está vazia. Adicione séries e filmes pelo Descobrir.",
  "library.empty.watchlist.overline": "Quero ver",
  "library.empty.watchlist.body": "Sua lista Quero ver está vazia. Salve títulos no Descobrir para assistir depois.",
  "library.empty.watching.overline": "Assistindo",
  "library.empty.watching.body": "Nada em andamento. Escolha sua próxima maratona pelo Descobrir.",
  "library.empty.completed.overline": "Finalizados",
  "library.empty.completed.body": "Nada finalizado ainda. Os títulos que você terminar aparecem aqui.",
  "library.empty.dropped.overline": "Abandonados",
  "library.empty.dropped.body": "Nada abandonado. Os títulos que você parar de assistir aparecem aqui.",
  "library.error.load": "Não foi possível carregar sua biblioteca",
  "library.status.watchlist": "Quero ver",
  "library.status.watching": "Assistindo",
  "library.status.completed": "Finalizado",
  "library.status.dropped": "Abandonado",
  "library.status.rated": "Nota {{rating}} de 5",

  // Show detail
  "show.tabs.sectionsA11y": "Seções do título",
  "show.tabs.details": "Detalhes",
  "show.tabs.discussion": "Discussão",
  "show.credits": "Créditos",
  "show.directedBy": "Direção",
  "show.createdBy": "Criação",
  "show.minutes": "{{minutes}} min",
  "show.titleOverline": "Título",
  "show.loading": "Carregando título",
  "show.error.load": "Não foi possível carregar este título",
  "show.unavailable": "Este título não está disponível",
  "show.unavailableBody": "Ele pode ter sido removido do catálogo.",
  "show.episodes": "Episódios",
  "show.seasonsSection": "Temporadas",
  "show.jumpToSeason": "Ir para a temporada",
  "show.seasonChip": "Temporada {{number}}",
  "show.seasonChipCompleted": "Temporada {{number}}, finalizada",
  "show.episodesOf": "{{watched}} de {{total}} episódios",
  "show.seasonCompleted": "finalizada",
  "show.nextEpisode": "Próximo",
  "show.watchAll": "Marcar todos",
  "show.unwatchAll": "Desmarcar todos",
  "show.loadingSeasons": "Carregando temporadas",
  "show.loadingEpisodes": "Carregando episódios",
  "show.error.seasons": "Não foi possível carregar as temporadas.",
  "show.error.episodes": "Não foi possível carregar os episódios.",
  "show.noEpisodeDetails": "Ainda não há detalhes dos episódios.",
  "show.markWatchedA11y": "Marcar {{label}} como assistido",
  "show.markUnwatchedA11y": "Desmarcar {{label}}",
  "show.ratingForA11y": "Sua nota para {{label}}",
  "show.discussA11y": "Discutir {{label}}",
  "show.similar": "Semelhantes",
  "show.loadingSimilar": "Carregando títulos semelhantes",
  "show.tracking.statusLabel": "Status",
  "show.tracking.ratingLabel": "Sua nota",
  "show.tracking.ratingValueA11y": "Sua nota: {{rating}} de 5",
  "show.tracking.addToLibrary": "Adicionar à biblioteca",
  "show.tracking.removeFromLibrary": "Remover da biblioteca",
  "show.tracking.progressA11y": "Progresso dos episódios",
  "show.tracking.ofEpisodes": "de {{total}} episódios",
  "show.tracking.episodesLeft": "{{count}} episódios restantes",
  "show.tracking.episodesLeft_singular": "{{count}} episódio restante",
  "show.tracking.status.wantToWatch": "Quero ver",
  "show.tracking.status.watching": "Assistindo",
  "show.tracking.status.finished": "Finalizado",
  "show.tracking.status.dropped": "Abandonado",
  "show.tracking.status.watched": "Assistido",
  "show.tracking.statusA11y": "Status: {{status}}",
  "show.tracking.statusSheetA11y": "Status",
  "show.action.markWatched": "Marcar como assistido",
  "show.action.watchAgain": "Assistir novamente",
  "show.action.markFinished": "Marcar como finalizado",
  "show.action.start": "Começar {{target}}",
  "show.action.startWatching": "Começar a assistir",
  "show.action.continueLabel": "Continuar {{target}}",
  "show.action.continueShort": "Continuar",
  "show.trailer.title": "Trailer",
  "show.trailer.play": "Reproduzir trailer",
  "show.error.updateStatus": "Não foi possível atualizar o status",
  "show.error.markEpisodeWatched": "Não foi possível marcar o episódio como assistido",
  "show.error.unmarkEpisode": "Não foi possível desmarcar o episódio",
  "show.error.markSeasonWatched": "Não foi possível marcar a temporada",
  "show.error.unmarkSeason": "Não foi possível desmarcar a temporada",
  "show.error.markFinished": "Não foi possível marcar como finalizado",
  "show.error.startRewatch": "Não foi possível começar a reassistir",
  "show.error.rate": "Não foi possível salvar a nota",

  // Rating stars
  "component.rating.rated": "Avaliado {{value}} de 5",
  "component.rating.rateStar": "Avaliar com {{value}} de 5",
  "component.rating.unrated": "Sem nota",

  // Discover
  "discover.title": "Descobrir",
  "discover.searchField": "Buscar séries e filmes",
  "discover.searchScope": "Escopo da busca",
  "discover.scope.all": "Tudo",
  "discover.scope.shows": "Séries",
  "discover.scope.movies": "Filmes",
  "discover.clear": "Limpar",
  "discover.loadingGenres": "Carregando gêneros",
  "discover.browseByGenre": "Navegar por gênero",
  "discover.searching": "Buscando",
  "discover.error.search": "Não foi possível buscar agora",
  "discover.error.load": "Não foi possível carregar o Descobrir",
  "discover.noMatches": "Nenhum resultado",
  "discover.noMatchesBody":
    "Nada encontrado para \u201c{{query}}\u201d. Tente outro título ou navegue por um gênero.",
  "discover.clearSearch": "Limpar busca",
  "discover.resultCount": "{{count}} resultados para \u201c{{query}}\u201d",
  "discover.resultCount_singular": "{{count}} resultado para \u201c{{query}}\u201d",
  "discover.resultCountNoQuery": "{{count}} resultados",
  "discover.resultCountNoQuery_singular": "{{count}} resultado",
  "discover.loadingMoreResults": "Carregando mais resultados",
  "discover.loadingBrowse": "Carregando em alta e mais bem avaliados",
  "discover.browseOverline": "Navegar",
  "discover.browseEmpty": "Títulos em alta e mais bem avaliados aparecerão aqui.",
  "discover.trending": "Em Alta",
  "discover.topRated": "Mais Bem Avaliados",
  "discover.recommended": "Achamos que você vai gostar",
  "discover.scrollTrendingLeft": "Rolar em alta para a esquerda",
  "discover.scrollTrendingRight": "Rolar em alta para a direita",
  "discover.scrollTopRatedLeft": "Rolar mais bem avaliados para a esquerda",
  "discover.scrollTopRatedRight": "Rolar mais bem avaliados para a direita",
  "discover.scrollRecommendedLeft": "Rolar recomendados para a esquerda",
  "discover.scrollRecommendedRight": "Rolar recomendados para a direita",
  "discover.genreClose": "Fechar gênero",
  "discover.genreOverline": "Gênero",
  "discover.titlesCount": "{{count}} títulos",
  "discover.titlesCount_singular": "{{count}} título",
  "discover.sortGenreResults": "Ordenar resultados do gênero",
  "discover.sortPopular": "Populares",
  "discover.sortTopRated": "Mais bem avaliados",
  "discover.error.genre": "Não foi possível carregar este gênero",
  "discover.genreEmpty": "Ainda não há títulos neste gênero.",
  "discover.backToDiscover": "Voltar para Descobrir",
  "discover.loadingMoreGenre": "Carregando mais títulos de {{genre}}",
  "discover.loadingGenreTitles": "Carregando títulos de {{genre}}",
  "discover.stats.rating": "Nota {{rating}} de 5",
  "discover.stats.discussionCount": "{{count}} comentários",

  // Calendar
  "calendar.eyebrow": "O que está no ar",
  "calendar.title": "Calendário",
  "calendar.previousMonth": "Mês anterior",
  "calendar.nextMonth": "Próximo mês",
  "calendar.daysA11y": "Dias de {{month}}",
  "calendar.today": "hoje",
  "calendar.episodesAiring": "{{count}} episódios no ar",
  "calendar.episodeAiring": "1 episódio no ar",
  "calendar.noEpisodesOn": "Nenhum episódio em {{day}}",
  "calendar.episodesOn": "{{count}} episódios em {{day}}",
  "calendar.episodeOn": "{{count}} episódio em {{day}}",
  "calendar.noEpisodesMineDay": "Nenhum episódio das suas séries vai ao ar neste dia.",
  "calendar.comingUp": "Próximos",
  "calendar.loading": "Carregando seu calendário",
  "calendar.emptyBody":
    "Novos episódios das séries que você acompanha aparecem aqui conforme forem ao ar.",
  "calendar.error.load": "Não foi possível carregar seu calendário",

  // Friends feed
  "friends.title": "Amigos",
  "friends.eyebrow": "Veja o que seus amigos assistem",
  "friends.feedSource": "Origem do feed",
  "friends.fromFriends": "Dos amigos",
  "friends.loadingFeed": "Carregando o feed",
  "friends.error.load": "Não foi possível carregar o feed",
  "friends.nothingRecent": "Nada recente",
  "friends.findPeopleToFollow": "Encontrar pessoas para seguir",
  "friends.emptyRecentBody": "As pessoas que você segue não registraram nada recentemente. Volte em breve.",
  "friends.emptyNoFriendsBody": "Busque pelo nome de usuário para ver o que seus amigos estão assistindo.",
  "friends.findPeople": "Encontrar pessoas",
  "friends.nothingTrending": "Nada em alta ainda",
  "friends.trendingEmptyBody":
    "O Em Alta reúne os comentários mais curtidos dos títulos da sua biblioteca, de todo mundo e não só de quem você segue. Quando os títulos que você acompanha esquentarem, a conversa aparece aqui.",
  "friends.exploreDiscover": "Explorar o Descobrir",
  "friends.aFriend": "Um amigo",
  "friends.openDiscussionFor": "Abrir discussão de {{title}}",
  "friends.verb.watched": "assistiu",
  "friends.verb.rewatched": "reassistiu",
  "friends.verb.finished": "terminou",
  "friends.watchedEpisodesOf": "assistiu {{count}} episódios de",
  "friends.commentedOn": "comentou em",
  "friends.deletedComment": "Este comentário foi excluído.",
  "friends.someone": "Alguém",
  "friends.filmMeta": "Filme · {{time}}",
  "friends.episodeSummaryMore": "{{summary}} +{{count}} mais",

  // Connections
  "connections.people": "Pessoas",
  "connections.title": "Conexões",
  "connections.listA11y": "Lista de conexões",
  "connections.findPeople": "Encontrar pessoas",
  "connections.kind.followers": "seguidores",
  "connections.kind.following": "seguindo",
  "connections.kind.requests": "solicitações",
  "connections.followers": "Seguidores",
  "connections.following": "Seguindo",
  "connections.requests": "Solicitações",
  "connections.loading": "Carregando {{kind}}",
  "connections.error.load": "Não foi possível carregar {{kind}}",
  "connections.personCount": "{{count}} pessoa em {{kind}}",
  "connections.personCount_plural": "{{count}} pessoas em {{kind}}",
  "connections.empty.followers.title": "Nenhum seguidor ainda",
  "connections.empty.followers.body": "Quando alguém seguir você, aparece aqui.",
  "connections.empty.following.title": "Você ainda não segue ninguém",
  "connections.empty.following.body": "Siga pessoas para ver o que elas estão assistindo.",
  "connections.empty.requests.title": "Nenhuma solicitação pendente",
  "connections.empty.requests.body": "Quando alguém pedir para seguir você, revise aqui.",
  "connections.member": "Membro",
  "connections.follow": "Seguir",
  "connections.followBack": "Seguir de volta",
  "connections.followingState": "Seguindo",
  "connections.unfollow": "Deixar de seguir",
  "connections.unfollowA11y": "Deixar de seguir @{{username}}",
  "connections.followBackA11y": "Seguir @{{username}} de volta",
  "connections.approve": "Aceitar",
  "connections.decline": "Recusar",
  "connections.approveA11y": "Aceitar solicitação de @{{username}}",
  "connections.declineA11y": "Recusar solicitação de @{{username}}",

  // People search
  "people.title": "Encontrar pessoas",
  "people.searchLabel": "Nome de usuário ou nome",
  "people.searchPlaceholder": "Buscar pessoas",
  "people.searchOverline": "Buscar",
  "people.idle.title": "Buscar pessoas",
  "people.idle.body": "Encontre amigos pelo nome de usuário ou nome para ver o que estão assistindo.",
  "people.searchingFor": "Buscando por {{term}}",
  "people.error.search": "Não foi possível buscar pessoas",
  "people.noResults": "Nenhuma pessoa encontrada para \u201c{{term}}\u201d",
  "people.noResultsBody": "A busca considera o início de nomes e nomes de usuário. Confira a ortografia e tente de novo.",
  "people.foundCount": "{{count}} pessoa encontrada para {{term}}",
  "people.foundCount_plural": "{{count}} pessoas encontradas para {{term}}",
  "people.followsYou": "Segue você",
  "people.privateBadge": "Privado",
  "people.requested": "Solicitado",

  // User profile (shared shelf)
  "user.viewingStats": "Estatísticas",
  "user.loadingStats": "Carregando estatísticas de {{name}}",
  "user.error.stats": "Não foi possível carregar as estatísticas de {{name}}",
  "user.recentWatches": "Assistidos recentemente",
  "user.loadingRecent": "Carregando recentes",
  "user.error.recent": "Não foi possível carregar os recentes",
  "user.nothingLogged": "Nada registrado ainda.",
  "user.library": "Biblioteca",
  "user.libraryMeta": "{{total}} títulos · {{watching}} assistindo · {{completed}} finalizados",
  "user.loadingLibrary": "Carregando a biblioteca",
  "user.error.library": "Não foi possível carregar a biblioteca",
  "user.emptyShelf": "Esta biblioteca está vazia.",
  "user.table.title": "Título",
  "user.table.status": "Status",
  "user.table.progress": "Progresso",
  "user.table.rating": "Nota",
  "user.status.removed": "Removido",
  "user.rewatched": "Reassistido ×{{count}}",
  "user.private.lock": "Este perfil é privado",
  "user.private.hint": "Somente seguidores aprovados podem ver a bio, as estatísticas, a atividade e a biblioteca de {{name}}.",
  "user.error.load": "Não foi possível carregar este perfil",
  "user.notFound": "Usuário não encontrado",
  "user.notFoundBody": "Esta conta não existe, ou o nome de usuário pode ter mudado.",
  "user.followersCount": "seguidores",
  "user.followingCount": "seguindo",
  "user.loadingProfile": "Carregando perfil",

  // Profile (own)
  "profile.overline": "Seu cartão",
  "profile.edit": "Editar perfil",
  "profile.request": "Solicitação",
  "profile.loading": "Carregando seu perfil",
  "profile.error.load": "Não foi possível carregar seu perfil",
  "profile.loadingStats": "Carregando suas estatísticas",
  "profile.error.stats": "Não foi possível carregar suas estatísticas",
  "profile.stats.timeWatched": "Tempo assistido",
  "profile.stats.slow": "Ainda fazendo as contas…",
  "profile.stats.trackedSummary": "{{completed}} finalizados · {{titles}} acompanhados",
  "profile.stats.episodesWatched": "Episódios assistidos",
  "profile.stats.movieViewings": "Filmes assistidos",
  "profile.stats.seriesWatched": "Séries assistidas",
  "profile.stats.bestStreak": "Melhor sequência (dias)",
  "profile.stats.activity": "Atividade",
  "profile.stats.activeDays": "{{count}} dias ativos",
  "profile.stats.activeDays_singular": "{{count}} dia ativo",
  "profile.stats.dayStreak": "Sequência de {{count}} dias",
  "profile.stats.genres": "Seus gêneros",
  "profile.stats.genresSubtitle": "Participação no tempo assistido",
  "profile.stats.estimateNote": "Algumas durações ausentes no catálogo usam uma estimativa.",
  "profile.stats.undatedNote": "{{count}} visualizações antigas entram nos totais, mas não têm data para o gráfico.",
  "profile.stats.undatedNote_singular": "{{count}} visualização antiga entra nos totais, mas não tem data para o gráfico.",
  "profile.stats.activityA11y": "Atividade do último ano, com {{count}} dias ativos",
  "profile.stats.activityA11y_singular": "Atividade do último ano, com {{count}} dia ativo",
  "profile.stats.activityEmpty": "Assista a algo para começar seu mapa de atividade.",
  "profile.stats.noWatchTime": "sem tempo assistido",
  "profile.stats.less": "Menos",
  "profile.stats.more": "Mais",
  "profile.settings": "Configurações",
  "profile.appearance": "Aparência",
  "profile.appearanceHint": "O tema é aplicado na hora e fica salvo neste dispositivo.",
  "profile.language": "Idioma",
  "profile.languageHint": "Links e conteúdo localizado seguem este idioma.",
  "profile.notifications": "Notificações",
  "profile.session": "Sessão",
  "profile.sessionHint": "Sair do ScreenAtlas neste dispositivo.",
  "profile.signOut": "Sair",
  "profile.dangerZone": "Zona de perigo",
  "profile.dangerHint":
    "Excluir sua conta remove permanentemente sua biblioteca, seu histórico e seu perfil. Não dá para desfazer.",
  "profile.deleteAccount": "Excluir conta",
  "profile.privacyPolicy": "Política de Privacidade",
  "profile.about": "Sobre",
  "profile.yourComments": "Seus comentários",
  "profile.commentsEmpty": "Você ainda não comentou.",
  "profile.commentsLoading": "Carregando seus comentários",
  "profile.commentsError": "Não foi possível carregar seus comentários",
  "profile.commentsSeeAll": "Ver todos ({{count}})",
  "profile.deleteDialog.description":
    "Isso exclui permanentemente sua conta, sua biblioteca e seu histórico. Não há como desfazer.",
  "profile.deleteDialog.typeUsername": "Digite @{{username}} ou DELETE para confirmar",
  "profile.deleteDialog.typeDelete": "Digite DELETE para confirmar",
  "profile.deleteDialog.queueUnavailable":
    "A fila de exclusão está indisponível. Nada foi excluído. Tente novamente.",
  "profile.deleteDialog.inProgress": "A exclusão da conta já está em andamento.",
  "profile.deleteDialog.failed": "Não foi possível iniciar a exclusão. Tente novamente.",

  // Your data (import/export)
  "profile.yourData": "Seus dados",
  "profile.yourDataHint": "Leve sua biblioteca com você, ou traga um histórico de exibição.",
  "profile.yourData.notAvailable": "A importação e exportação de dados não está disponível agora.",
  "profile.yourData.export": "Exportar seus dados",
  "profile.yourData.exporting": "Gerando sua exportação…",
  "profile.yourData.exportQueued":
    "Vamos enviar um link de download por e-mail, e ele também aparecerá aqui.",
  "profile.yourData.download": "Baixar",
  "profile.yourData.emailed": "Também enviamos este link por e-mail.",
  "profile.yourData.exportFailed": "Não foi possível gerar sua exportação. Tente novamente.",
  "profile.yourData.exportRateLimited": "Você atingiu o limite diário de exportações. Tente mais tarde.",
  "profile.yourData.exportInProgress": "Uma exportação já está em andamento. Aguarde um instante.",
  "profile.yourData.import": "Importar",
  "profile.yourData.importHint":
    "Traga seu histórico do TV Time, Trakt, Letterboxd, Simkl, de um CSV ou de uma exportação do ScreenAtlas.",
  "profile.yourData.importing": "Importando…",
  "profile.yourData.detecting": "Detectando o formato…",
  "profile.yourData.uploading": "Enviando… {{percent}}%",
  "profile.yourData.goToLibrary": "Ir para sua biblioteca",
  "profile.yourData.unmatched": "Baixar não encontrados ({{count}})",
  "profile.yourData.duplicate": "Este arquivo já foi importado. Nada novo foi adicionado.",
  "profile.yourData.importFailed": "Não foi possível importar este arquivo. Tente novamente.",
  "profile.yourData.importRateLimited": "Você atingiu o limite diário de importações. Tente mais tarde.",
  "profile.yourData.importInProgress": "Uma importação já está em andamento. Aguarde um instante.",
  "profile.yourData.importPending": "Você tem uma importação aguardando para começar.",
  "profile.yourData.resume": "Retomar",
  "profile.yourData.recentReady": "Sua exportação de {{time}} está pronta",
  "profile.yourData.recentHint": "Este link de download funciona por 7 dias.",

  // Import wizard (choose a source → upload → process → done)
  "profile.import.source.title": "De onde são seus dados?",
  "profile.import.source.tvtime.name": "TV Time",
  "profile.import.source.tvtime.upload":
    "o .zip baixado do TV Time (ou o CSV de acompanhamento)",
  "profile.import.source.trakt.name": "Trakt",
  "profile.import.source.trakt.upload": "o .zip de Configurações → Dados → Exportar",
  "profile.import.source.letterboxd.name": "Letterboxd",
  "profile.import.source.letterboxd.upload": "o .zip de Configurações → Importar & Exportar (Pro)",
  "profile.import.source.simkl.name": "Simkl",
  "profile.import.source.simkl.upload": "o backup .zip/.json de Configurações → Backup",
  "profile.import.source.screenatlas.name": "Exportação do ScreenAtlas",
  "profile.import.source.screenatlas.upload": "um export .json do ScreenAtlas",
  "profile.import.source.generic.name": "Outro CSV",
  "profile.import.source.generic.upload":
    "um CSV com título, temporada, episódio (e data/nota opcionais), ou título + ano para filmes",
  "profile.import.source.notSure": "Não tenho certeza",
  "profile.import.upload.hint": "Até 50 MB. Detectamos o formato automaticamente.",
  "profile.import.upload.choose": "Escolher arquivo",
  "profile.import.upload.guide": "Como exportar do {{source}}",
  "profile.import.help.tvtime":
    "Abra o TV Time e vá em Configurações.\nEscolha Exportar dados e baixe o .zip.\nOu use o CSV de acompanhamento.",
  "profile.import.help.trakt":
    "Abra o Trakt e vá em Configurações.\nEscolha Dados → Exportar.\nBaixe o arquivo .zip.",
  "profile.import.help.letterboxd":
    "Abra o Letterboxd e vá em Configurações.\nEscolha Importar & Exportar (Pro).\nBaixe seus dados como .zip.",
  "profile.import.help.simkl":
    "Abra o Simkl e vá em Configurações.\nEscolha Backup.\nBaixe o backup .zip ou .json.",
  "profile.import.help.screenatlas":
    "Baixe um export .json do ScreenAtlas no app ou neste site.",
  "profile.import.help.generic":
    "Um CSV com uma coluna de título. Inclua temporada e episódio para séries ou o ano para filmes.",
  "profile.import.detected": "Formato detectado: {{format}}",
  "profile.import.hintMismatch": "Isso parecia um arquivo de {{format}}, então importamos como tal.",
  "profile.import.count.shows": "séries",
  "profile.import.count.movies": "filmes",
  "profile.import.count.episodes": "episódios",
  "profile.import.count.ratings": "notas",
  "profile.import.count.watchlist": "lista de desejos",
  "profile.import.count.rewatches": "reassistidas",
  "profile.import.report.title": "Não conseguimos encontrar tudo",
  "profile.import.report.show": "série",
  "profile.import.report.movie": "filme",
  "profile.import.report.rows": "{{count}} linhas",
  "profile.import.report.download": "Baixar relatório",
  "profile.import.again": "Importar outro arquivo",
  "profile.import.failUnsupported": "Não reconhecemos esse formato de arquivo.",
  "profile.import.failArchive": "Não foi possível ler esse arquivo. Ele pode ser inválido ou grande demais.",
  "profile.import.failTooLarge": "Esse arquivo tem linhas demais para importar.",
  "profile.import.failSourceKey": "O upload não foi concluído corretamente. Tente novamente.",

  // Notification preferences
  "notif.prefs.hint": "Escolha quais atividades de comentários chegam até você.",
  "notif.prefs.mentions": "Menções",
  "notif.prefs.mentionsHint": "Alguém me menciona em um comentário",
  "notif.prefs.replies": "Respostas",
  "notif.prefs.repliesHint": "Alguém responde meu comentário",
  "notif.prefs.saving": "Salvando…",
  "notif.prefs.saved": "Salvo",
  "notif.prefs.error": "Não foi possível salvar. As alterações foram desfeitas.",
  "notif.prefs.loadError": "Não foi possível carregar as preferências de notificação.",
  "notif.prefs.saveError": "Não foi possível salvar as preferências de notificação",

  // Edit profile
  "profile.edit.back": "Perfil",
  "profile.edit.title": "Editar perfil",
  "profile.edit.loading": "Carregando o editor",
  "profile.edit.name": "Nome",
  "profile.edit.nameHint": "Até {{count}} caracteres. Aparece ao lado de @{{username}} na busca.",
  "profile.edit.saveName": "Salvar nome",
  "profile.edit.bio": "Bio",
  "profile.edit.bioHint": "Até {{count}} caracteres.",
  "profile.edit.saveBio": "Salvar bio",
  "profile.edit.saveError": "Não foi possível salvar. Tente novamente.",
  "profile.socials.title": "Redes sociais",
  "profile.socials.hint": "Opcional. Aparecem no seu perfil. Cole o @ ou o link do perfil.",
  "profile.socials.instagram": "Instagram",
  "profile.socials.tiktok": "TikTok",
  "profile.socials.x": "X",
  "profile.socials.invalid": "Esse não é um usuário válido do {{network}}.",
  "profile.socials.open": "{{network}}: @{{handle}}",
  "profile.socials.save": "Salvar redes sociais",
  "profile.socials.saveError": "Não foi possível salvar suas redes sociais",
  "profile.edit.username": "Nome de usuário",
  "profile.edit.usernameHint": "Você pode alterar seu nome de usuário uma vez por hora.",
  "profile.edit.cooldown": "Você poderá alterar seu nome novamente em {{date}}.",
  "profile.edit.usernameRules": "Use de 3 a 20 caracteres: letras de a a z, números de 0 a 9 ou _",
  "profile.edit.checking": "Verificando…",
  "profile.edit.available": "Este nome está disponível",
  "profile.edit.taken": "Este nome já está em uso",
  "profile.edit.reserved": "Este nome é reservado",
  "profile.edit.cooldownState": "Este nome está em resfriamento",
  "profile.edit.checkError": "Não foi possível verificar agora. Tente novamente.",
  "profile.edit.changeUsername": "Alterar nome de usuário",
  "profile.edit.confirmTitle": "Alterar nome de usuário?",
  "profile.edit.confirmBody":
    "Seus links @{{username}} antigos deixarão de funcionar. Você não poderá alterar o nome por 1 hora.",
  "profile.edit.renameTaken": "Este nome acabou de ser escolhido. Tente outro.",
  "profile.edit.renameError": "Não foi possível alterar o nome. Tente novamente.",
  "profile.edit.photo": "Foto de perfil",
  "profile.edit.photoHint": "Aparece no seu cartão, ao lado dos seus comentários e para quem segue você.",
  "profile.edit.photoPreview": "Prévia da foto selecionada",
  "profile.edit.choosePhoto": "Escolher uma foto de perfil",
  "profile.edit.changePhoto": "Trocar foto",
  "profile.edit.uploading": "Enviando…",
  "profile.edit.photoFormat": "JPEG, PNG ou WebP de até 5 MB.",
  "profile.edit.uploadPhoto": "Enviar foto",
  "profile.edit.wrongType": "Escolha uma imagem JPEG, PNG ou WebP.",
  "profile.edit.tooLarge": "Essa imagem tem mais de 5 MB. Escolha uma menor.",
  "profile.edit.uploadError": "Não foi possível trocar a foto. Tente novamente.",

  // Discussion
  "discussion.title": "Discussão",
  "discussion.sortBy": "Ordenar comentários",
  "discussion.sortTop": "Top",
  "discussion.sortNew": "Recentes",
  "discussion.startConversation": "Comece a conversa",
  "discussion.emptyBody": "Nenhum comentário ainda. Seja a primeira pessoa a dizer o que acha.",
  "discussion.error.load": "Não foi possível carregar a discussão",
  "discussion.loading": "Carregando a discussão",
  "discussion.deleteTitle": "Excluir este comentário?",
  "discussion.deleteBody": "Não dá para desfazer.",
  "discussion.deletedAuthor": "[excluído]",
  "discussion.you": "Você",
  "discussion.edited": "(editado)",
  "discussion.profileA11y": "Perfil de {{name}}",
  "discussion.upvote": "Curtir ({{count}})",
  "discussion.removeUpvote": "Remover curtida ({{count}})",
  "discussion.reply": "Responder",
  "discussion.optionsFor": "Opções do comentário de @{{username}}",
  "discussion.optionsForThis": "Opções deste comentário",
  "discussion.edit": "Editar",
  "discussion.report": "Denunciar",
  "discussion.hideReplies": "Ocultar respostas",
  "discussion.repliesCount": "{{count}} resposta",
  "discussion.repliesCount_plural": "{{count}} respostas",
  "discussion.error.replies": "Não foi possível carregar as respostas.",
  "discussion.loadingMoreReplies": "Carregando mais respostas…",
  "discussion.loadMoreReplies": "Carregar mais respostas",
  "discussion.editComment": "Editar comentário",
  "discussion.attachingImage": "Anexando imagem…",
  "discussion.imageA11y": "Ver imagem anexada em tamanho cheio",
  "discussion.replyingTo": "Respondendo a",
  "discussion.thisComment": "este comentário",
  "discussion.cancelReply": "Cancelar resposta",
  "discussion.replyToA11y": "Responder a {{target}}",
  "discussion.writeCommentA11y": "Escrever um comentário",
  "discussion.replyPlaceholder": "Escreva uma resposta…",
  "discussion.composerPlaceholder": "Participe da discussão…",
  "discussion.mentionSuggestions": "Pessoas para mencionar",
  "discussion.post": "Publicar",
  "discussion.imagePreview": "Prévia da imagem selecionada",
  "discussion.removeImage": "Remover imagem",
  "discussion.attachImage": "Anexar uma imagem",
  "discussion.chooseImage": "Escolher um arquivo de imagem",
  "discussion.imageTooLarge": "A imagem deve ter 5 MB ou menos.",
  "discussion.reportTitle": "Denunciar este comentário",
  "discussion.reportBody": "Um moderador vai dar uma olhada.",
  "discussion.reasonLegend": "Motivo",
  "discussion.reason.spoiler": "Spoiler",
  "discussion.reason.spam": "Spam",
  "discussion.reason.harassment": "Assédio",
  "discussion.reason.abuse": "Abuso",
  "discussion.reason.other": "Outra coisa",
  "discussion.reportSubmitted": "Denúncia enviada. Obrigado por avisar.",
  "discussion.error.episode": "Não foi possível carregar este episódio",
  "discussion.episodeUnavailable": "Este episódio não está disponível",
  "discussion.episodeUnavailableBody": "Confira o endereço e tente novamente.",
  "discussion.loadingEpisode": "Carregando episódio",
  "discussion.episodeFallback": "Episódio {{number}}",
  "discussion.error.post": "Não foi possível publicar o comentário",
  "discussion.error.postReply": "Não foi possível publicar a resposta",
  "discussion.error.attachImage": "Não foi possível anexar a imagem",
  "discussion.error.vote": "Não foi possível atualizar a curtida",
  "discussion.error.edit": "Não foi possível salvar a edição",
  "discussion.error.report": "Não foi possível enviar a denúncia",

  // Notifications
  "notif.overline": "Atividade",
  "notif.title": "Notificações",
  "notif.unreadCount": "{{count}} não lidas",
  "notif.markAllRead": "Marcar todas como lidas",
  "notif.allRead": "Todas as notificações marcadas como lidas",
  "notif.loading": "Carregando notificações",
  "notif.error.load": "Não foi possível carregar notificações",
  "notif.empty": "Você está em dia",
  "notif.emptyBody": "Quando alguém segue você, pede para seguir ou responde seus comentários, aparece aqui.",
  "notif.follow": "começou a seguir você",
  "notif.followRequest": "solicitou para seguir você",
  "notif.approved": "aprovou sua solicitação de seguir",
  "notif.declined": "recusou sua solicitação de seguir",
  "notif.resolved": "solicitação resolvida",
  "notif.commentReply": "respondeu seu comentário",
  "notif.commentMention": "mencionou você em um comentário",
  "notif.upvotedComment": "curtiu seu comentário",
  "notif.generic": "Nova atividade",
  "notif.unreadBadge": "Não lida",
  "notif.approving": "Aceitando…",
  "notif.declining": "Recusando…",
  "notif.approvedState": "Aceita",
  "notif.declinedState": "Recusada",
  "notif.approveA11y": "Aceitar solicitação de {{name}}",
  "notif.declineA11y": "Recusar solicitação de {{name}}",
  "notif.markReadError": "Não foi possível marcar como lidas",
  "notif.error.generic": "Algo deu errado",

  // Lists
  "lists.overline": "Coleções",
  "lists.title": "Listas",
  "lists.newList": "Nova lista",
  "lists.createList": "Criar lista",
  "lists.loading": "Carregando suas listas",
  "lists.error.load": "Não foi possível carregar suas listas",
  "lists.empty":
    "Agrupe títulos em coleções (séries de conforto, para assistir de novo, para ver com alguém) e deixe tudo a um clique.",
  "lists.createFirst": "Criar primeira lista",
  "lists.count": "{{count}} títulos",
  "lists.count_singular": "{{count}} título",
  "lists.updatedAt": "Atualizada em {{date}}",
  "lists.moveUp": "Mover {{name}} para antes",
  "lists.moveDown": "Mover {{name}} para depois",
  "lists.movedUp": "{{name}} movida para antes",
  "lists.movedDown": "{{name}} movida para depois",
  "lists.limitReached": "Limite de listas atingido. Exclua uma para liberar espaço.",
  "lists.nameLimit": "Os nomes têm limite de 50 caracteres.",
  "lists.createError": "Não foi possível criar a lista. Tente novamente.",
  "lists.overSingle": "Coleção",
  "lists.addTitles": "Adicionar títulos",
  "lists.actionsA11y": "Ações da lista {{name}}",
  "lists.rename": "Renomear",
  "lists.deleteList": "Excluir lista",
  "lists.renameTitle": "Renomear lista",
  "lists.deleteTitle": "Excluir lista?",
  "lists.deleteBody": "\u201c{{name}}\u201d será excluída. Os títulos dela continuam na sua biblioteca.",
  "lists.deleting": "Excluindo…",
  "lists.loadingDetail": "Carregando a lista",
  "lists.emptyTitle": "Lista vazia",
  "lists.emptyBody": "Nada aqui ainda. Adicione séries e filmes para começar esta coleção.",
  "lists.notFound": "Lista não encontrada",
  "lists.notFoundBody": "Esta lista não existe mais. Ela pode ter sido excluída.",
  "lists.backToLists": "Voltar para Listas",
  "lists.error.loadDetail": "Não foi possível carregar esta lista",
  "lists.actionsFor": "Ações de {{title}}",
  "lists.moveEarlier": "Mover antes",
  "lists.moveLater": "Mover depois",
  "lists.removeFromList": "Remover da lista",
  "lists.movedEarlier": "{{title}} movido antes",
  "lists.movedLater": "{{title}} movido depois",
  "lists.removed": "{{title}} removido da lista",
  "lists.loadMore": "Carregar mais",
  "lists.renameError": "Não foi possível renomear a lista. Tente novamente.",
  "lists.name": "Nome",
  "lists.namePlaceholder": "Séries de conforto",
  "lists.nameRequired": "Dê um nome à lista.",
  "lists.description": "Descrição",
  "lists.descriptionPlaceholder": "Uma nota que só você verá",
  "lists.saving": "Salvando…",
  "lists.addTo": "Adicionar a {{name}}",
  "lists.searchTitles": "Buscar títulos",
  "lists.searchPlaceholder": "Buscar séries e filmes",
  "lists.added": "{{title}} adicionado a {{list}}",
  "lists.removedFrom": "{{title}} removido de {{list}}",
  "lists.removeFromA11y": "Remover {{title}} de {{list}}",
  "lists.addToA11y": "Adicionar {{title}} a {{list}}",
  "lists.inList": "Na lista",
  "lists.add": "Adicionar",
  "lists.searchHint": "Busque no catálogo e toque em Adicionar. Os títulos que já estão na coleção aparecem marcados.",
  "lists.noMatches": "Nada encontrado para \u201c{{query}}\u201d. Tente outra grafia ou o nome de alguém do elenco.",
  "lists.error.rename": "Não foi possível renomear a lista",
  "lists.error.delete": "Não foi possível excluir a lista",
  "lists.error.reorder": "Não foi possível reordenar suas listas",
  "lists.error.reorderShows": "Não foi possível reordenar a lista",
  "lists.error.add": "Não foi possível adicionar o título",
  "lists.error.remove": "Não foi possível remover o título",

  // Social follow toasts
  "social.error.follow": "Não foi possível seguir @{{username}}",
  "social.error.unfollow": "Não foi possível deixar de seguir @{{username}}",
  "social.error.approve": "Não foi possível aceitar a solicitação",
  "social.error.decline": "Não foi possível recusar a solicitação",

  // Auth — threshold layout (shared)
  "auth.marquee.title": "Tudo o que você assiste, numa biblioteca só.",
  "auth.marquee.body": "Séries, filmes e cada episódio no meio do caminho, sincronizados entre a web, o iPhone e o Android.",
  "auth.homeLink": "Página inicial do ScreenAtlas",
  "auth.orEmail": "ou use seu e-mail",
  "auth.showPassword": "Mostrar senha",
  "auth.hidePassword": "Ocultar senha",
  "auth.google": "Google",
  "auth.apple": "Apple",
  "auth.pw.title": "Sua senha precisa ter",
  "auth.pw.length": "8 ou mais caracteres",
  "auth.pw.upper": "Uma letra maiúscula",
  "auth.pw.lower": "Uma letra minúscula",
  "auth.pw.digit": "Um número",
  "auth.pw.met": "ok",
  "auth.pw.unmet": "faltando",
  "login.heading": "Que bom te ver de novo",
  "login.lede": "Entre para continuar de onde parou.",
  "signup.heading": "Crie sua conta grátis",
  "signup.lede": "Acompanhe cada série e filme que você assiste.",
  "signup.legalBefore": "Ao criar uma conta, você concorda com a",
  "signup.legalLink": "política de privacidade",
  "signup.privacyHref": "/br/privacidade",
  "confirm.resendIn": "Reenviar em {{count}}s",
  "confirm.differentEmail": "Usar outro e-mail",
  "forgot.lede": "Vamos enviar um código de 6 dígitos para você criar uma nova senha.",
  "forgot.stepOf": "Passo {{step}} de 2",

  // Auth — login
  "login.signIn": "Entrar",
  "login.email": "E-mail",
  "login.password": "Senha",
  "login.forgotPasswordLink": "Esqueceu a senha?",
  "login.newToScreenAtlas": "Novo no ScreenAtlas?",
  "login.createAccount": "Criar uma conta",
  "login.continueWithGoogle": "Continuar com o Google",
  "login.continueWithApple": "Continuar com a Apple",
  "login.signedInAs": "Conectado como",
  "login.continueToApp": "Continuar para o app",
  "login.signOut": "Sair",
  "login.challengeBefore": "Esta conta ainda precisa de uma nova senha. Use",
  "login.challengeLink": "redefinir senha",
  "login.challengeAfter": "para definir uma e entrar de novo.",
  "login.error.enterEmail": "Digite seu e-mail.",
  "login.error.enterPassword": "Digite sua senha.",
  "login.error.invalid": "E-mail ou senha inválidos.",
  "login.error.network": "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.",
  "login.error.unknown": "Algo deu errado. Tente novamente.",

  // Auth — signup
  "signup.title": "Criar conta",
  "signup.emailValid": "Digite um e-mail válido.",
  "signup.emailExists": "Já existe uma conta com este e-mail.",
  "signup.weakPassword":
    "Essa senha não atende aos requisitos. Use 8+ caracteres com uma letra maiúscula, uma minúscula e um número.",
  "signup.haveAccount": "Já tem uma conta?",
  "signup.perksLabel": "O que você ganha",
  "signup.perk.free": "Grátis na web, no iPhone e no Android",
  "signup.perk.search": "Busque qualquer série ou filme",
  "signup.perk.library": "Uma biblioteca só: assistindo, quero ver, finalizados",
  "signup.perk.episodes": "Marque episódios e nunca perca onde parou",

  // Auth — confirm
  "confirm.title": "Confira seu e-mail",
  "confirm.codeSent": "Enviamos um código de 6 dígitos para {{email}}.",
  "confirm.codeSentBefore": "Enviamos um código de 6 dígitos para",
  "confirm.noEmail": "Digite o e-mail usado no cadastro e o código de 6 dígitos que enviamos.",
  "confirm.code": "Código de confirmação",
  "confirm.resend": "Reenviar código",
  "confirm.submit": "Confirmar",
  "confirm.enterEmail": "Digite seu endereço de e-mail.",
  "confirm.enterCode": "Digite o código de 6 dígitos.",
  "confirm.success": "Conta confirmada. Entre para continuar.",
  "confirm.codeFail": "Esse código não funcionou. Tente novamente.",
  "confirm.resendSuccess": "Um novo código está a caminho.",
  "confirm.resendFail": "Não foi possível reenviar o código.",

  // Auth — forgot
  "forgot.title": "Redefinir sua senha",
  "forgot.prompt": "Digite o e-mail da sua conta e enviaremos um código de redefinição.",
  "forgot.sendCode": "Enviar código de redefinição",
  "forgot.resetCode": "Código de redefinição",
  "forgot.newPassword": "Nova senha",
  "forgot.setNewPassword": "Definir nova senha",
  "forgot.success": "Senha atualizada. Entre com sua nova senha.",
  "forgot.resetFail": "Não foi possível redefinir a senha.",
  "forgot.remembered": "Lembrou, afinal?",

  // Auth callback
  "auth.callback.failedTitle": "Não foi possível concluir o login",
  "auth.callback.failedBody": "Algo deu errado ao concluir o login social. Tente novamente.",
  "auth.callback.back": "Voltar para entrar",
  "auth.callback.signingIn": "Entrando…",

  // 404
  "error404.title": "Página não encontrada",
  "error404.body": "Essa página não existe. Sua biblioteca está a um clique.",

  // E — friends/social/notif
  "connections.backToProfile": "Voltar ao perfil",
  "friends.results": "{{count}} resultado",
  "friends.results_plural": "{{count}} resultados",

  // D — library/discover/show/discussion
  "library.count": "{{count}} títulos",
  "library.count_singular": "{{count}} título",
  "library.summary.watching": "{{count}} assistindo",
  "library.summary.completed": "{{count}} finalizados",
  "library.summary.completed_singular": "{{count}} finalizado",
  "library.summary.watchlist": "{{count}} quero ver",
  "show.tracking.regionA11y": "Seu progresso",
  "show.scrollSimilarLeft": "Rolar similares para a esquerda",
  "show.scrollSimilarRight": "Rolar similares para a direita",
  "episode.code": "T{{season}} · E{{episode}}",

  // F — profile/calendar/lists
  "calendar.empty": "Nada agendado por enquanto",
  "lists.noLists": "Nenhuma lista ainda",

  // T2 — inbox
  "inbox.title": "Discussões",
  "inbox.tabsLabel": "Conversas a exibir",
  "inbox.tab.replies": "Respostas",
  "inbox.tab.mentions": "Menções",
  "inbox.tab.yours": "Seus",
  "inbox.tab.friends": "Amigos",
  "inbox.tabUnread": ", {{count}} novas",
  "inbox.listLabel": "Conversas: {{tab}}",
  "inbox.loading": "Carregando conversas",
  "inbox.error.list": "Não foi possível carregar suas conversas",
  "inbox.empty.replies.title": "Nenhuma resposta ainda",
  "inbox.empty.replies.body": "Quando alguém responder a um comentário seu, a resposta aparece aqui.",
  "inbox.empty.mentions.title": "Nenhuma menção ainda",
  "inbox.empty.mentions.body": "Quando alguém marcar você com @ num comentário, aparece aqui.",
  "inbox.empty.yours.title": "Você ainda não comentou",
  "inbox.empty.yours.body": "Seus comentários sobre séries, filmes e episódios ficam reunidos aqui, para você acompanhar as respostas.",
  "inbox.empty.friends.title": "Nada dos amigos por enquanto",
  "inbox.empty.friends.body": "Os comentários recentes de quem você segue aparecem aqui.",
  "inbox.empty.goTonight": "Ver o que tem para hoje",
  "inbox.empty.findPeople": "Encontrar pessoas para seguir",
  "inbox.pick.title": "Escolha uma conversa",
  "inbox.pick.body": "Selecione uma resposta ou discussão à esquerda para ler aqui.",
  "inbox.thread.loading": "Carregando a discussão",
  "inbox.thread.missingTitle": "Essa conversa não está aqui",
  "inbox.thread.missingBody": "Ela pode estar mais abaixo na lista, ou o comentário foi removido.",
  "inbox.thread.unknownTitle": "Não dá para abrir essa discussão aqui",
  "inbox.thread.unknownBody": "A notificação não diz a qual discussão ela pertence.",
  "inbox.backToList": "Discussões",
  "inbox.kicker.discussion": "Discussão",
  "inbox.kicker.show": "Discussão da série",
  "inbox.kicker.movie": "Discussão do filme",
  "inbox.kicker.season": "Temporada {{season}}",
  "inbox.seasonCode": "T{{season}}",
  "inbox.openEpisode": "Página do episódio",
  "inbox.openTitle": "Página do título",
  "inbox.aDiscussion": "uma discussão",
  "inbox.unread": "Nova:",
  "inbox.withImage": "Com imagem",
  "inbox.snippet.image": "Enviou uma imagem",
  "inbox.snippet.replyDeleted": "Resposta apagada",
  "inbox.snippet.commentDeleted": "Comentário apagado",
  "inbox.snippet.hidden": "Oculto pela moderação",
  "inbox.snippet.replied": "Respondeu ao seu comentário",
  "inbox.snippet.mentioned": "Mencionou você num comentário",
  "notif.emptyPeopleBody": "Quando alguém segue você ou pede para seguir, aparece aqui. Respostas e menções ficam em Discussões.",
  "notif.openDiscussions": "Abrir Discussões",
  "discussion.anchorMissing": "Não encontramos esse comentário aqui. Ele pode ter sido apagado ou moderado.",
  "discussion.anchorA11y": "(o comentário que você abriu)",

  // T1 — tonight/nav/palette
  "nav.tonight": "Esta noite",
  "nav.discussions": "Discussões",
  "nav.calendar": "Calendário",
  "nav.notifications": "Notificações",
  "nav.notificationsUnread": "Notificações, {{count}} não lidas",
  "nav.unreadReplies": "{{label}}, {{count}} respostas não lidas",
  "nav.search": "Buscar",
  "nav.searchA11y": "Buscar títulos, pessoas e listas ({{shortcut}})",
  "nav.searchField": "Buscar títulos, pessoas, listas",
  "nav.searchTooltip": "Buscar {{shortcut}}",
  "nav.collapse": "Recolher barra lateral",
  "nav.expand": "Expandir barra lateral",
  "nav.shelves": "Suas estantes",
  "nav.shelfCount": "{{label}}, {{count}} títulos",
  "nav.listCount": "{{name}}, {{count}} títulos",
  "nav.allLists": "Todas as listas",
  "nav.upNextA11y": "A seguir: {{title}}, {{code}}",
  "nav.upNextTitleA11y": "A seguir: {{title}}",
  "nav.shortcuts": "Atalhos de teclado",
  "nav.shortcutsDescription": "Atalhos de uma tecla ficam desligados enquanto você digita num campo.",
  "nav.shortcutSearch": "Buscar títulos, pessoas e listas",
  "nav.shortcutCollapse": "Recolher ou expandir a barra lateral",
  "nav.shortcutEpisodes": "Numa lista de episódios",
  "tonight.greeting.morning": "{{day}} de manhã",
  "tonight.greeting.afternoon": "{{day}} à tarde",
  "tonight.greeting.evening": "{{day}} à noite",
  "tonight.hello.morning": "Bom dia, {{name}}",
  "tonight.hello.afternoon": "Boa tarde, {{name}}",
  "tonight.hello.evening": "Boa noite, {{name}}",
  "tonight.upNext": "A seguir",
  "tonight.upNextEmpty": "Nada em andamento ainda",
  "tonight.upNextEmptyBody": "Comece uma série e o próximo episódio fica esperando por você aqui.",
  "tonight.exploreDiscover": "Explorar o Descobrir",
  "tonight.loadingUpNext": "Carregando o que vem a seguir",
  "tonight.openShow": "Abrir série",
  "tonight.alsoInProgress": "Também em andamento",
  "tonight.alsoScrollLeft": "Rolar Também em andamento para a esquerda",
  "tonight.alsoScrollRight": "Rolar Também em andamento para a direita",
  "tonight.stream": "Dos seus amigos",
  "tonight.newItems": "{{count}} novidades",
  "tonight.newItemsA11y": "{{count}} novidades dos amigos. Use o botão no topo do feed para ver.",
  "tonight.loadingFeed": "Carregando a atividade dos seus amigos",
  "tonight.empty.noFriends": "A noite fica melhor com amigos",
  "tonight.empty.noFriendsBody": "Siga pessoas que você conhece para ver aqui o que elas assistem, avaliam e comentam.",
  "tonight.empty.quiet": "Nada novo dos amigos por enquanto",
  "tonight.empty.quietBody": "Quando quem você segue assistir, avaliar ou comentar algo, aparece aqui.",
  "tonight.findPeople": "Encontrar pessoas",
  "tonight.sidebar": "Agora",
  "tonight.watchingNow": "Amigos assistindo agora",
  "tonight.loadingWatching": "Carregando quem está assistindo",
  "tonight.watchingA11y": "{{name}} está assistindo {{title}}",
  "tonight.airingThisWeek": "No ar esta semana",
  "tonight.loadingAiring": "Carregando os episódios da semana",
  "tonight.nothingAiring": "Nada do que você acompanha estreia nos próximos sete dias.",
  "tonight.seeCalendar": "Calendário",
  "tonight.hotOnShelf": "Em alta na sua biblioteca",
  "tonight.upvotes": "Votos:",
  "tonight.stars": "{{rating}} de 5 estrelas",
  "tonight.addWatchlist": "Adicionar ao Quero ver",
  "tonight.addWatchlistA11y": "Adicionar {{title}} ao Quero ver",
  "tonight.inWatchlist": "Na sua lista Quero ver",
  "tonight.inWatchlistA11y": "{{title}} está na sua lista Quero ver",
  "tonight.verb.rated": "avaliou",
  "tonight.finishedSeries": "terminou a série",
  "tonight.watchedEpisodesOf": "assistiu a {{count}} episódios de",
  "tonight.upvoteA11y": "Votar, {{count}} até agora",
  "tonight.reply": "Responder",
  "tonight.replyTo": "Responder a {{name}}",
  "tonight.postReply": "Publicar resposta",
  "tonight.replyPosted": "Resposta publicada.",
  "tonight.viewThread": "Ver discussão",
  "tonight.viewThreadCount": "Ver discussão ({{count}})",
  "tonight.becauseYouFinished": "Porque você terminou",
  "tonight.becauseA11y": "Porque você terminou {{title}}",
  "tonight.error.upNext": "Não foi possível carregar o que vem a seguir",
  "tonight.error.feed": "Não foi possível carregar a atividade dos seus amigos",
  "tonight.error.partial": "Parte da atividade dos seus amigos não carregou.",
  "tonight.error.watching": "Não foi possível carregar quem está assistindo",
  "tonight.error.airing": "Não foi possível carregar os episódios da semana",
  "upnext.continue": "Continuar",
  "upnext.continueA11y": "Continuar {{title}}: marcar {{code}} como assistido",
  "upnext.markWatchedA11y": "Marcar {{code}} de {{title}} como assistido",
  "upnext.codeCompact": "T{{season}}·E{{episode}}",
  "upnext.episodeLong": "Temporada {{season}}, episódio {{episode}}",
  "upnext.progressOf": "{{watched}} de {{total}}",
  "upnext.progressA11y": "Progresso em {{title}}",
  "upnext.caughtUp": "Tudo em dia",
  "upnext.new": "Novo",
  "palette.title": "Buscar no ScreenAtlas",
  "palette.description": "Encontre um título, uma pessoa ou uma das suas listas, ou vá direto para uma página.",
  "palette.inputA11y": "Buscar títulos, pessoas e listas",
  "palette.placeholder": "Buscar títulos, pessoas, listas…",
  "palette.searching": "Buscando…",
  "palette.empty": "Nada corresponde a “{{query}}”.",
  "palette.titles": "Títulos",
  "palette.people": "Pessoas",
  "palette.lists": "Suas listas",
  "palette.recent": "A seguir",
  "palette.goTo": "Ir para",
  // T3 — library
  "library.heading": "Sua biblioteca",
  "library.quick.group": "Ações rápidas para {{title}}",
  "library.quick.rate": "Avaliar {{title}}",
  "library.quick.rated": "Sua nota para {{title}}: {{rating}} de 5",
  "library.quick.rateShort": "Avaliar",
  "library.quick.status": "Status de {{title}}: {{status}}",
  "library.quick.statusShort": "Status",
  "library.quick.remove": "Remover…",
  "library.quick.removeTitle": "Remover {{title}}?",
  "library.quick.removeBody": "O título sai da sua biblioteca e o progresso é apagado.",
  "library.quick.removeConfirm": "Remover",
  "upnext.title": "A seguir",
  "upnext.scrollLeft": "Rolar A seguir para a esquerda",
  "upnext.scrollRight": "Rolar A seguir para a direita",
  // T4 — discover
  "discover.spotlight.loading": "Carregando uma sugestão para você",
  "discover.spotlight.addWatchlist": "Adicionar ao Quero ver",
  "discover.spotlight.addWatchlistA11y": "Adicionar {{title}} ao Quero ver",
  "discover.spotlight.onWatchlist": "Na sua lista Quero ver",
  "discover.becauseWatched": "Porque você assistiu {{title}}",
  "discover.scrollShelfLeft": "Rolar “{{shelf}}” para a esquerda",
  "discover.scrollShelfRight": "Rolar “{{shelf}}” para a direita",
  "discover.genres": "Gêneros",
  "discover.paletteLoadError": "Não foi possível abrir a busca. Verifique sua conexão e tente de novo.",

  // Sharing (docs/sharing-plan.md §11)
  "share.action.a11y": "Compartilhar",
  "share.copyLink": "Copiar link",
  "share.share": "Compartilhar…",
  "share.recommend": "Recomendar para alguém…",
  "share.recommendDialog.title": "Recomendar para alguém",
  "share.shareOnX": "Compartilhar no X",
  "share.shareOnWhatsApp": "Compartilhar no WhatsApp",
  "share.notePlaceholder": "Adicione uma nota (opcional)",
  "share.send": "Enviar",
  "share.copied": "Link copiado",
  "share.recommendedBy": "{{name}} acha que você vai gostar",
  "share.error.create": "Não foi possível criar o link de compartilhamento",
  "share.unavailable": "Link indisponível",
  "share.unavailableBody": "Esse link de compartilhamento não é válido ou não está mais disponível.",
  "share.goToTonight": "Ir para Esta noite",
  "share.preview.overline": "Título compartilhado",
  "share.preview.recommendation": "{{name}} acha que você vai gostar de {{title}}",
  "share.preview.openTitle": "Abrir no ScreenAtlas",
  "share.preview.logIn": "Entrar",
  "share.preview.createAccount": "Criar conta",
  "share.preview.openInApp": "Abrir no app",
  // Configuração do perfil (features/onboarding) — espelha o setup.* do app.
  "setup.title": "Configure seu perfil",
  "setup.progress": "Etapa {{step}} de {{total}}",
  "setup.next": "Próximo",
  "setup.done": "Concluir",
  "setup.skip": "Pular",
  "setup.language.title": "Escolha seu idioma",
  "setup.language.body": "Você pode mudar quando quiser nas Configurações do perfil.",
  "setup.photo.title": "Adicione uma foto de perfil",
  "setup.photo.body": "Seus amigos te reconhecem mais rápido no feed e nas discussões.",
  "setup.photo.choose": "Escolher foto",
  "setup.name.title": "Como devemos te chamar?",
  "setup.name.body": "Seu nome aparece no perfil e ao lado dos seus comentários.",
  "setup.username.title": "Escolha seu nome de usuário",
  "setup.username.body": "O atual foi gerado automaticamente. Um de verdade facilita te encontrar e te @mencionar.",
  "setup.bio.title": "Conte qual é a sua",
  "setup.bio.body": "Uma linha sobre o que você mais gosta de assistir.",
  "setup.socials.title": "Onde mais te encontrar?",
  "setup.socials.body": "Opcional. Adicione seu Instagram, TikTok ou X para os amigos te seguirem por lá também.",
  "setup.error": "Não deu para salvar. Tente de novo ou pule por enquanto.",
  "setup.replay.title": "Introdução",
  "setup.replay.hint": "Refaça a configuração do perfil: o idioma e o que ainda estiver faltando.",
  "setup.replay.action": "Rever introdução",
  // Redesign 2026-10 — Discussions, Calendar, Profile, Settings, Notifications
  "inbox.verb.replied": "te respondeu em",
  "inbox.verb.mentioned": "mencionou você em",
  "inbox.verb.commented": "comentou em",
  "inbox.verb.you": "Você comentou em",
  "nav.settings": "Configurações",
  "settings.general": "Geral",
  "settings.account": "Conta",
  "settings.loadingNotifications": "Carregando preferências de notificação",
  "profile.stats.trackedLine": "{{completed}} finalizados, {{titles}} acompanhados",
  "user.shelfItemA11y": "{{title}}, {{status}}",
  // Library refresh — toolbar + Filter sheet
  "library.typeA11y": "Tipo: {{type}}",
  "library.sheet.open": "Filtrar",
  "library.sheet.title": "Filtrar e ordenar",
  "library.sheet.type": "Tipo",
  "library.sheet.view": "Exibição",
  "library.view.grid": "Grade",
  "library.view.list": "Lista",

  // Title page redesign + rating prompt
  "show.meta.seasons": "{{count}} temporadas",
  "show.meta.seasons_singular": "{{count}} temporada",
  "show.meta.episodes": "{{count}} episódios",
  "show.meta.episodes_singular": "{{count}} episódio",
  "show.synopsis.more": "Mais",
  "show.synopsis.less": "Menos",
  "show.action.addToWatchlist": "Quero ver",
  "show.action.rateIt": "Avalie",
  "show.status.chooseA11y": "Escolher um status",
  "show.remove.menu": "Remover…",
  "show.remove.title": "Remover {{title}}?",
  "show.remove.body": "O título sai da sua biblioteca e o histórico de episódios é apagado.",
  "show.remove.confirm": "Remover",
  "show.lists.add": "Adicionar a uma lista",
  "show.lists.added": "Adicionado a {{list}}",
  "show.lists.empty": "Você ainda não tem listas",
  "show.lists.manage": "Suas listas…",
  "show.lists.loading": "Carregando suas listas",
  "show.lists.error": "Não foi possível carregar suas listas",
  "show.cast": "Elenco",
  "show.cast.scrollLeft": "Rolar elenco para a esquerda",
  "show.cast.scrollRight": "Rolar elenco para a direita",
  "show.cast.seeAll": "Ver elenco completo ({{count}})",
  "show.cast.seeLess": "Mostrar menos",
  "show.aside.a11y": "Sobre este título",
  "show.facts.heading": "Sobre",
  "show.facts.status": "Situação",
  "show.facts.status.airing": "Em exibição",
  "show.facts.status.ended": "Encerrada",
  "show.facts.status.upcoming": "Em breve",
  "show.facts.network": "Canal",
  "show.facts.firstAired": "Estreia",
  "show.facts.released": "Lançamento",
  "show.facts.runtime": "Duração",
  "show.facts.genres": "Gêneros",
  "show.facts.score": "Nota no TMDB",
  "show.facts.scoreValue": "{{score}} de 5",
  "show.shortcuts.label": "Atalhos",
  "show.shortcuts.next": "Próximo episódio",
  "show.shortcuts.previous": "Episódio anterior",
  "show.shortcuts.toggle": "Marcar como visto ou não visto",
  "ratingPrompt.title": "O que achou de {{title}}?",
  "ratingPrompt.starsA11y": "Avaliar {{title}}",
  "ratingPrompt.notNow": "Agora não",
  "ratingPrompt.rated": "Avaliado com {{count}} estrelas",
  "ratingPrompt.rated_singular": "Avaliado com {{count}} estrela",
  "ratingPrompt.undo": "Desfazer",
  "discover.spotlight.open": "Abrir",
  "discover.spotlight.reasonFinished": "Porque você terminou {{title}}",
  "user.shelf.total": "{{count}} títulos",
  "user.shelf.watching": "{{count}} assistindo",
  "user.shelf.completed": "{{count}} finalizados",
  "discover.searchingFor": "Buscando \u201c{{query}}\u201d\u2026",
  // People pass (profile, shelf, connections, lists)
  "user.followersCount_singular": "seguidor",
  "profile.commentsMore": "Mostrar mais",
  "profile.commentsEmptyAction": "Participar de uma discussão",
  "calendar.day.today": "Hoje",
  "calendar.day.tomorrow": "Amanhã",
  "calendar.day.yesterday": "Ontem",
  "calendar.nextAiring": "Próximo episódio: {{day}}",
  "share.preview.joinPitch": "Acompanhe o que você assiste. De graça na web, no iPhone e no Android.",
  "auth.error.expiredCode": "Esse código expirou. Solicite um novo.",
  "auth.error.tooMany": "Muitas tentativas. Espere alguns minutos e tente de novo.",
  "auth.socialError": "Não foi possível entrar com {{provider}}. Tente de novo.",
  // Discussions / episode thread / notifications UI pass.
  "discussion.newCommentPill": "{{count}} novo",
  "discussion.newCommentsPill": "{{count}} novos",
  "discussion.newComments": "Novos comentários: {{count}}. Use o botão acima da caixa de comentário para ver.",
  "discussion.loadingMore": "Carregando mais comentários",
  "discussion.writeFirst": "Escrever o primeiro comentário",
  "inbox.empty.openWatching": "Abrir uma série que você está vendo",
};

export default ptBR;
